// Tie first, then withdraw what is left. (ZEN-791, ZEN-796)
//
// Maryse chose option A on 21 Oct 2026: 529 money already withdrawn and tied to
// no expense is taken off what the app suggests withdrawing. On 23 Oct 2026 she
// settled the order: expenses already incurred first, then the costs due by the
// end of next month (ZEN-796). Her rulings:
//   * only the SAME child's untied money, and only from the SAME tax year;
//   * money requested but not yet received is taken off too (it lands in a day
//     or two; a cancelled one is corrected by the owner);
//   * one 529 account per child, so accounts are never compared;
//   * the form warns, it never blocks.
// Every screen that suggests an amount (Overview Coming up, the Expenses totals,
// the withdrawal form) asks this file, so they cannot disagree.
import { untiedOf, untiedYear } from "@/lib/untiedWithdrawals";
import { NAV_STATE_KEY } from "@/lib/withdrawThisAmount";

const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

/** The child's untied withdrawals whose money can be tied to costs of `year`. */
export function sameYearUntied(withdrawals, studentId, year) {
  if (!studentId || !year) return [];
  return untiedOf(withdrawals).filter(
    (w) => w.student_id === studentId && untiedYear(w) === Number(year));
}

/** How the suggested amount changes, or null when nothing is untied or owed.
 *
 *  INCURRED FIRST (ZEN-796, Maryse 23 Oct 2026): the untied money is tied first
 *  to expenses already incurred and awaiting a draw, and only what is left over
 *  goes toward the costs due by the end of next month.
 *
 *  `incurred` is what is already incurred and could be drawn now; `due` is what
 *  falls due by the end of next month. `left` is what the plan suggests
 *  withdrawing now; `covers` means nothing is left to withdraw now. `stage` is
 *  "past" when the untied money goes only to incurred expenses, "upcoming" when
 *  some of it (or, with nothing incurred, all of it) goes to the costs due, and
 *  "all" when it covers both. */
export function tieFirstPlan({ incurred = 0, due, withdrawals, studentId, year }) {
  const rows = sameYearUntied(withdrawals, studentId, year);
  const untied = round2(rows.reduce((t, w) => t + Number(w.untied_usd), 0));
  const past = round2(incurred > 0.005 ? incurred : 0);
  const owed = round2(due > 0.005 ? due : 0);
  if (!(untied > 0.005) || !(past + owed > 0.005)) return null;
  const toPast = round2(Math.min(untied, past));
  const spare = round2(untied - toPast);
  let stage, left;
  if (past > 0.005 && !(spare > 0.005)) {
    // One withdrawal for both: what the incurred expenses still need, plus
    // everything due (Maryse, 24 Oct 2026).
    stage = "past"; left = round2(past - toPast + owed);
  } else if (spare + 0.005 < owed) {
    stage = "upcoming"; left = round2(owed - spare);
  } else {
    stage = "all"; left = 0;
  }
  if (!(left > 0.005)) left = 0;
  const readyNow = round2(past - toPast);
  return { incurred: past, due: owed, untied, toPast, spare, readyNow, left, covers: left === 0, stage, rows };
}

/** SAME TAX YEAR ONLY. Every withdraw suggestion offers only expenses of the
 *  tax year it is for: "Expenses and withdrawals should be in the SAME year
 *  unless the user asks for an exception, this should not be offered"
 *  (Maryse, 21 Oct 2026). Of a figure's rows ({id, usd}), the ones whose
 *  expense (looked up in `byId`) is filed under `year`. */
export function rowsOfYear(rows, byId, year) {
  return (rows || []).filter((r) => taxYearOf(byId.get(r.id)) === Number(year));
}

/** The tax year an expense is filed under: its stored tax year, else the year
 *  of its date. The one test every screen here uses for "same tax year". */
export const taxYearOf = (e) => Number(e?.eligibility_tax_year) || Number(String(e?.transaction_date || "").slice(0, 4));

/** "the expenses due by the end of November", singular when one is due.
 *  Wording approved by Maryse, 21 Oct 2026. */
export const dueWords = (count, monthName) =>
  `the expense${count === 1 ? "" : "s"} due by the end of ${monthName}`;

/** Opening the new-withdrawal form for what is left: the amount only, never a
 *  choice of expenses, because which expenses the untied money is tied to is
 *  the owner's choice. */
export const withdrawLeftNavState = (plan, monthName) => ({
  [NAV_STATE_KEY]: {
    amountOnly: plan.left.toFixed(2),
    plan: { incurred: plan.incurred || 0, due: plan.due, untied: plan.untied, left: plan.left,
            stage: plan.stage, monthName, ids: plan.rows.map((w) => w.id) },
  },
});

// THE WORDS. Every sentence about the untied money lives here, so a change of
// wording is made once. With nothing incurred they are the sentences Maryse
// approved on 21 Oct 2026 (ZEN-791); the incurred-first ones she approved on
// 24 Oct 2026 (ZEN-796), with one withdrawal for incurred and upcoming together.
// Amounts come back as { b } parts so a card can
// show them in bold (and the ready-now amount in green, as before); `plain`
// joins them for a sentence with no styling.
const b = (text) => ({ b: text });
export const plain = (parts) => parts.map((p) => (typeof p === "string" ? p : p.b)).join("");

/** The Coming up card's sentence, and its second line (or null). */
export function comingUpWords(p, monthName, money) {
  const $ = (v) => b(money(v, "USD"));
  const head = ["529-eligible costs due by the end of ", monthName, " total ", $(p.due), ". "];
  if (!(p.incurred > 0.005)) {
    return {
      sentence: p.covers
        ? [...head, "The ", $(p.untied), " already withdrawn covers all of it, so there is nothing to withdraw for now."]
        : [...head, $(p.untied), " already withdrawn can cover part of it, so ", $(p.left), " is left to withdraw."],
      readyLine: null,
    };
  }
  if (p.stage === "past") {
    return {
      sentence: [...head, "Time a withdrawal so the funds are ready before they hit."],
      readyLine: p.readyNow > 0.005
        ? [{ ...$(p.readyNow), green: true }, " is ready to withdraw now, once the ", $(p.untied), " already withdrawn is tied to expenses already incurred."]
        : null,
    };
  }
  if (p.stage === "upcoming") {
    return {
      sentence: [...head, "The ", $(p.untied), " already withdrawn covers the ", $(p.incurred), " already incurred, and the ",
        $(p.spare), " left over can cover part of ", monthName, "'s, so ", $(p.left), " is left to withdraw."],
      readyLine: null,
    };
  }
  return {
    sentence: [...head, "The ", $(p.untied), " already withdrawn covers them and the ", $(p.incurred), " already incurred, so there is nothing to withdraw for now."],
    readyLine: null,
  };
}

/** The Coming up card's tie button. */
/** The Overview's combined "529 Withdrawals" card, its Next step part
 *  (ZEN-814, Maryse 26 and 28 Oct 2026). Her sentence and buttons for the one
 *  situation they were written for — the untied money covers what is already
 *  incurred and part of what falls due. Every other situation keeps the
 *  Coming up box's approved sentence, word for word ("approve both", 28 Oct). */
export function nextStepWords(p, monthName, money) {
  const $ = (v) => b(money(v, "USD"));
  if (p.incurred > 0.005 && p.stage === "upcoming") {
    return {
      sentence: ["You withdrew ", $(p.untied), " that isn't tied to expenses yet. It covers the ", $(p.toPast),
        " already paid, and ", $(p.spare), " of the ", $(p.due), " due by the end of ", monthName,
        ". Tie it first, then withdraw the last ", $(p.left), "."],
      readyLine: null,
      tieLabel: `Tie the ${money(p.untied, "USD")}`,
    };
  }
  return { ...comingUpWords(p, monthName, money), tieLabel: tieButtonWords(p, money) };
}

export const tieButtonWords = (p, money) =>
  (p.stage === "past" ? `Tie the ${money(p.untied, "USD")} to expenses already incurred` : `Tie the ${money(p.untied, "USD")} first`);

/** The Expenses page's line in the amber untied box. */
export function expensesReminderWords(p, dueCount, monthName, money) {
  if (!(p.incurred > 0.005)) {
    return p.covers
      ? `Tie it to any of the expense(s) due by the end of ${monthName}. Nothing more needs withdrawing for them.`
      : `Tie it to ${dueWords(dueCount, monthName)} first. After that, ${money(p.left, "USD")} is left to withdraw.`;
  }
  if (p.stage === "past") {
    return p.covers
      ? "Tie it to expenses already incurred. Nothing more needs withdrawing for them."
      : p.due > 0.005
        ? `Tie it to expenses already incurred first. After that, ${money(p.left, "USD")} is left to withdraw, including ${dueWords(dueCount, monthName)}.`
        : `Tie it to expenses already incurred first. After that, ${money(p.left, "USD")} is left to withdraw now.`;
  }
  if (p.stage === "upcoming") {
    return `Tie it to expenses already incurred first, then to ${dueWords(dueCount, monthName)}. After that, ${money(p.left, "USD")} is left to withdraw.`;
  }
  return `Tie it to expenses already incurred and those due by the end of ${monthName}. Nothing more needs withdrawing for them.`;
}

/** The withdrawal form's sentence when it was opened for what is left. */
export function formPlanSentence(p, money) {
  const $ = (v) => money(v, "USD");
  if (!(p.incurred > 0.005)) {
    return `${$(p.left)} to withdraw = ${$(p.due)} due by the end of ${p.monthName} − ${$(p.untied)} already withdrawn. Tie the ${$(p.untied)} to any of the expense(s) due by then.`;
  }
  if (p.stage === "past") {
    return p.due > 0.005
      ? `${$(p.left)} to withdraw = ${$(p.incurred)} already incurred + ${$(p.due)} due by the end of ${p.monthName} − ${$(p.untied)} already withdrawn. Tie the ${$(p.untied)} to expenses already incurred.`
      : `${$(p.left)} to withdraw = ${$(p.incurred)} already incurred − ${$(p.untied)} already withdrawn. Tie the ${$(p.untied)} to expenses already incurred.`;
  }
  return `${$(p.left)} to withdraw = ${$(p.incurred)} already incurred + ${$(p.due)} due by the end of ${p.monthName} − ${$(p.untied)} already withdrawn. Tie the ${$(p.untied)} to expenses already incurred and to any of the expense(s) due by then.`;
}
